import type { BillingPeriod, PlanTier } from '../data/funnel/types'
import { queryClient } from '../lib/query'
import { handleApiError } from '../modules/core/services/api/api-error-handler'
import { store } from '../store'
import { buildApiError } from './api-error'
import { BIRDBRAIN_API_BASE_URL } from './birdbrain-api-config'

/**
 * Birdbrain API Client
 *
 * Authenticated fetch wrapper for birdbrain-api calls.
 * Reads the access token from Redux memory (post-cookie migration).
 * Includes automatic token refresh on 401 errors.
 */

export interface BirdbrainApiOptions extends RequestInit {
  // Additional options can be added here
}

/**
 * Internal fetch function for birdbrain-api
 * This is the core fetch logic that can be retried after token refresh
 */
async function performBirdbrainFetch<T = any>(
  endpoint: string,
  options: BirdbrainApiOptions = {}
): Promise<T> {
  const token = store.getState().authenticationView.accessToken

  const headers = new Headers(options.headers || {})
  headers.set('Content-Type', 'application/json')
  headers.set('User-Agent', 'BirdBrain-Admin/1.0')
  headers.set('X-Client-Type', 'web')

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const url = `${BIRDBRAIN_API_BASE_URL}${endpoint}`

  // Bearer-only API; sending credentials would tighten CORS unnecessarily.
  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    throw await buildApiError(response)
  }

  return response.json()
}

/**
 * Authenticated fetch wrapper for birdbrain-api
 * Includes JWT token in Authorization header and automatic token refresh
 */
export async function birdbrainApiFetch<T = any>(
  endpoint: string,
  options: BirdbrainApiOptions = {}
): Promise<T> {
  try {
    return await performBirdbrainFetch<T>(endpoint, options)
  } catch (error: any) {
    try {
      // Use the same error handling mechanism as the main API
      const result = await handleApiError(
        error,
        performBirdbrainFetch,
        [endpoint, options],
        null // No rejectWithValue for direct API calls
      )
      return result as T
    } catch (handledError) {
      // If error handler also fails, throw the original error with better messages
      if (error.status === 401) {
        throw new Error('Unauthorized - Please log in again')
      } else if (error.status === 403) {
        throw new Error('Forbidden - Admin access required')
      } else {
        throw error
      }
    }
  }
}

/**
 * Export the base URL for use in other parts of the app
 */
export { BIRDBRAIN_API_BASE_URL }

// Re-export storage functions for backwards compatibility
export { clearApiKey, getApiKey, storeApiKey } from '../modules/core/services/storage'

/**
 * Fetch function that uses API key authentication
 * This is used by TakaTV when running in fullscreen mode with an API key
 */
async function performApiKeyFetch<T = any>(
  endpoint: string,
  apiKey: string,
  options: BirdbrainApiOptions = {}
): Promise<T> {
  const headers = new Headers(options.headers || {})
  headers.set('Content-Type', 'application/json')
  headers.set('User-Agent', 'TakaTV/1.0')
  headers.set('X-API-Key', apiKey)

  const url = `${BIRDBRAIN_API_BASE_URL}${endpoint}`

  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    throw await buildApiError(response)
  }

  return response.json()
}

/**
 * API Key authenticated fetch wrapper for birdbrain-api
 * Does not attempt token refresh (API keys are long-lived)
 */
export async function apiKeyFetch<T = any>(
  endpoint: string,
  apiKey: string,
  options: BirdbrainApiOptions = {}
): Promise<T> {
  try {
    return await performApiKeyFetch<T>(endpoint, apiKey, options)
  } catch (error: any) {
    if (error.status === 401) {
      throw new Error('API Key is invalid or expired')
    } else if (error.status === 403) {
      throw new Error('API Key access denied - insufficient scope')
    } else {
      throw error
    }
  }
}

// ============================================
// Metrics API (from birdbrain-api / Snowflake)
// ============================================

export interface PlanBreakdown {
  plan: { pro: number; business: number }
  period: { monthly: number; annual: number }
}

export interface ArrMovement {
  dates: string[]
  newArr: number[]
  reactivationArr: number[]
  churnedEarlyArr: number[]
  churnedEstablishedArr: number[]
}

export interface ArrLevel {
  dates: string[]
  arr: number[]
}

export interface MixTrend {
  dates: string[]
  proPaid: number[]
  businessPaid: number[]
  monthlyPaid: number[]
  annualPaid: number[]
}

export interface BusinessMetrics {
  activeTrials: number
  activeSubscriptions: number
  mrr: number
  timestamp: string
  // Sparkline trend data (last 28 days)
  activeTrialsTrend: number[]
  activeSubscriptionsTrend: number[]
  mrrTrend: number[]
  // Chart data (last 28 days, daily)
  chartDates: string[]
  chartRevenue: number[]
  chartActiveSubscriptions: number[]
  chartActiveTrials: number[]
  // Plan breakdown
  planBreakdown: PlanBreakdown | null
  // ARR movement + level + subscription mix (~190 days daily)
  arrMovement: ArrMovement
  arrLevel: ArrLevel
  mixTrend: MixTrend
}

// ============================================
// Funnel Metrics Types
// ============================================

export interface FunnelBreakdownRow {
  name: string
  lpViewed: number
  lpCtaClicked: number
  appOpened: number
  signupCompleted: number
  onboardingCompleted: number
  activation: number
  trialStarted: number
  convertedToPay: number
  subscribed: number
}

export interface PreFunnelCampaign {
  campaign: string
  lpViewed: number
  lpCtaClicked: number
}

export interface PlatformWeekData {
  weekNumber: number
  weekLabel: string
  dateRange: string
  startDate: string
  endDate: string
  isCurrentWeek: boolean
  lpViewed: number
  lpCtaClicked: number
  appOpened: number
  signupCompleted: number
  onboardingCompleted: number
  activation: number
  trialStarted: number
  convertedToPay: number
  subscribed: number
  byPlatform: FunnelBreakdownRow[]
  byCampaign: FunnelBreakdownRow[]
  byCountry: FunnelBreakdownRow[]
  byPromoCode: FunnelBreakdownRow[]
  lpByCampaign: PreFunnelCampaign[]
}

export interface FunnelMetricsResponse {
  weeks: PlatformWeekData[]
  timestamp: string
}

export interface FetchFunnelMetricsParams {
  startDate?: string
  weeksCount?: number
}

// Funnel Users API types
export type FunnelStage =
  | 'app_opened'
  | 'signed_up'
  | 'onboarded'
  | 'subscribed'
  | 'activated'
  | 'trial_started'
  | 'converted'

export interface FunnelUser {
  userId: string
  email?: string
  name?: string
  platform: string
  campaign: string
  countryCode: string
  promoCode?: string
  firstSeen?: string
  appOpened: boolean
  signedUp: boolean
  onboarded: boolean
  activated: boolean
  trialStarted: boolean
  converted: boolean
  subscribed: boolean
  planTier?: PlanTier
  billingPeriod?: BillingPeriod
}

export interface SubscribeBreakdownPoint {
  weekStart: string
  subscribed: number
  viaTrial: number
  viaDirect: number
  trialConverted: number
  planTier: { pro: number; business: number; unknown: number }
  billingPeriod: { monthly: number; annual: number; unknown: number }
}

export interface SubscribeBreakdown {
  subscribed: number
  viaTrial: number
  viaDirect: number
  trialConverted: number
  planTier: { pro: number; business: number; unknown: number }
  billingPeriod: { monthly: number; annual: number; unknown: number }
  trend: SubscribeBreakdownPoint[]
}

export interface FetchSubscribeBreakdownParams {
  weekStart: string
  weeksBack?: number
  platform?: string
  campaign?: string
  country?: string
}

export interface FunnelUsersResponse {
  users: FunnelUser[]
  totalCount: number
  hasMore: boolean
  filter: {
    weekStart: string
    stage: FunnelStage
    platform?: string
    campaign?: string
    country?: string
  }
}

import type { FunnelUserSortField } from '@mondaygpt/constants'
export type { FunnelUserSortField } from '@mondaygpt/constants'

export interface FetchFunnelUsersParams {
  weekStart: string
  stage: FunnelStage
  platform?: string
  campaign?: string
  country?: string
  limit?: number
  offset?: number
  search?: string
  sortBy?: FunnelUserSortField
  sortOrder?: 'asc' | 'desc'
}

export const metricsApi = {
  async getBusinessMetrics(): Promise<BusinessMetrics> {
    return birdbrainApiFetch<BusinessMetrics>('/v1/birdbrain/metrics/business')
  },

  async getFunnelMetrics(params: FetchFunnelMetricsParams = {}): Promise<FunnelMetricsResponse> {
    const queryParams = new URLSearchParams()
    if (params.startDate) queryParams.set('startDate', params.startDate)
    if (params.weeksCount) queryParams.set('weeksCount', params.weeksCount.toString())

    const queryString = queryParams.toString()
    const endpoint = `/v1/birdbrain/metrics/funnel${queryString ? `?${queryString}` : ''}`
    return birdbrainApiFetch<FunnelMetricsResponse>(endpoint)
  },

  async getFunnelUsers(params: FetchFunnelUsersParams): Promise<FunnelUsersResponse> {
    const queryParams = new URLSearchParams()
    queryParams.set('weekStart', params.weekStart)
    queryParams.set('stage', params.stage)
    if (params.platform) queryParams.set('platform', params.platform)
    if (params.campaign) queryParams.set('campaign', params.campaign)
    if (params.country) queryParams.set('country', params.country)
    if (params.limit) queryParams.set('limit', params.limit.toString())
    if (params.offset) queryParams.set('offset', params.offset.toString())
    if (params.search) queryParams.set('search', params.search)
    if (params.sortBy) queryParams.set('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder)

    const queryString = queryParams.toString()
    const endpoint = `/v1/birdbrain/metrics/funnel/users?${queryString}`
    return birdbrainApiFetch<FunnelUsersResponse>(endpoint)
  },

  async getSubscribeBreakdown(params: FetchSubscribeBreakdownParams): Promise<SubscribeBreakdown> {
    const queryParams = new URLSearchParams()
    queryParams.set('weekStart', params.weekStart)
    if (params.weeksBack) queryParams.set('weeksBack', params.weeksBack.toString())
    if (params.platform) queryParams.set('platform', params.platform)
    if (params.campaign) queryParams.set('campaign', params.campaign)
    if (params.country) queryParams.set('country', params.country)

    const endpoint = `/v1/birdbrain/metrics/funnel/subscribe-breakdown?${queryParams.toString()}`
    return birdbrainApiFetch<SubscribeBreakdown>(endpoint)
  },
}

// ============================================
// User Activity API (from birdbrain-api / Snowflake)
// ============================================

export interface UserEvent {
  id: string
  eventName: string
  timestamp: string
  platform?: string
  city?: string
  region?: string
  countryCode?: string
  deviceModel?: string
  deviceManufacturer?: string
  os?: string
  osVersion?: string
  appVersion?: string
  browser?: string
  browserVersion?: number
  chatId?: string
  chatType?: string
  agentId?: string
  agentName?: string
  source?: string
  properties?: Record<string, unknown>
}

export interface UserActivityStats {
  totalEvents: number
  activeDays: number
  sessions: number
  messagesSent: number
  chatsCreated: number
  agentsCreated: number
  mostActivePlatform: string
  eventsByCategory: Record<string, number>
  firstEventAt?: string
  lastEventAt?: string
}

export interface UserActivityResponse {
  userId: string
  events: UserEvent[]
  stats: UserActivityStats
  totalCount: number
  hasMore: boolean
  page: number
  limit: number
  timestamp: string
}

export interface UserEventNamesResponse {
  userId: string
  eventNames: string[]
  timestamp: string
}

export interface DailyActivityResponse {
  dates: string[]
  series: Record<string, number[]>
  timestamp: string
}

export interface FetchUserActivityParams {
  userId: string
  page?: number
  limit?: number
  eventName?: string
  startDate?: string
  endDate?: string
}

export interface UserActivityRanking {
  userId: string
  totalEvents: number
  lastEventAt?: string
}

export interface MostActiveUsersResponse {
  users: UserActivityRanking[]
  totalCount: number
  timestamp: string
}

export interface UserAttribution {
  userId: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmTerm?: string
  initialReferrer?: string
  initialReferringDomain?: string
  platform?: string
  city?: string
  region?: string
  countryCode?: string
}

export interface UserAttributionResponse {
  attribution: UserAttribution | null
  found: boolean
  timestamp: string
}

// ============================================
// TakaTV API (dedicated TV dashboard endpoint)
// ============================================

export interface TvTrialConversionWeek {
  weekStart: string
  trialsStarted: number
  converted: number
  rate: number
  matured: boolean
}

export interface TvBusinessHealth {
  mrr: number
  activeSubscriptions: number
  activeTrials: number
  newMrr7Days: number | null
  churnedMrr7Days: number | null
  trialConversionWeekly: TvTrialConversionWeek[]
  // Sparkline trend data (last 28 days)
  mrrTrend: number[]
  activeSubscriptionsTrend: number[]
  activeTrialsTrend: number[]
}

export interface TvDailyGrowthMetric {
  label: string
  sublabel: string | null
  // Today's data (primary)
  today: number
  yesterday: number
  todayVsYesterday: number | null
  // Weekly context
  weekTotal: number
  daysInWeek: number
  dailyAverage: number
  weekOverWeekChange: number | null
  // Trends
  dailyTrend: number[]
  weeklyTrend: number[]
}

export interface TvFunnelStage {
  id: string
  label: string
  count: number
  conversionToNext: number
  weekOverWeekChange: number | null
  isPositiveChange: boolean
}

export interface TvCoreFunnel {
  stages: TvFunnelStage[]
  weekLabel: string
  dateRange: string
}

export interface TvPlatformBreakdown {
  name: string
  appOpened: number
  signups: number
  onboarded: number
  activations: number
  trialStarted: number
  converted: number
  signupRate: number
  activationRate: number
  trialRate: number
}

export interface TvCampaignBreakdown {
  name: string
  appOpened: number
  signupCompleted: number
  onboardingCompleted: number
  activation: number
  trialStarted: number
  convertedToPay: number
}

export interface TvLpCampaignBreakdown {
  campaign: string
  lpViewed: number
  lpCtaClicked: number
}

export interface TvActivationMetrics {
  activationRate: number
  activatedThisWeek: number
  medianTimeToActivation: number | null
  activationToTrialRate: number
  rateTrend: number[]
  trendLabels: string[]
  byPlatform: TvPlatformBreakdown[]
}

export interface TvMarketingMetrics {
  byCampaign: TvCampaignBreakdown[]
  weeklyAdSpend: number | null
  weeklyReach: number | null
}

export interface TvWeeklyDataPoint {
  weekNumber: number
  weekLabel: string
  dateRange: string
  startDate: string
  endDate: string
  isCurrentWeek: boolean
  lpViewed: number
  lpCtaClicked: number
  appOpened: number
  signupCompleted: number
  onboardingCompleted: number
  activation: number
  trialStarted: number
  convertedToPay: number
  byPlatform: TvPlatformBreakdown[]
  byCampaign: TvCampaignBreakdown[]
  lpByCampaign: TvLpCampaignBreakdown[]
}

export interface TvDailyCohortPoint {
  date: string
  signups: number
  subscribed: number
  paid: number
  /** Of subscribed-not-paid, how many are still in an active trial (potential payers) */
  inTrial: number
}

export interface TvDataFreshness {
  funnel: string | null
  business: string | null
  engagement: string | null
}

export interface TvMetricsResponse {
  businessHealth: TvBusinessHealth
  dailyGrowth: TvDailyGrowthMetric[]
  coreFunnel: TvCoreFunnel
  activation: TvActivationMetrics
  marketing: TvMarketingMetrics
  weeklyData: TvWeeklyDataPoint[]
  dailyCohortFunnel: TvDailyCohortPoint[]
  timestamp: string
  dataFreshness: TvDataFreshness
  version: string
}

export type TvActivityEvent = 'trial' | 'paid' | 'renewed' | 'churned' | 'topup'

export interface TvActivityItem {
  id: string
  businessId: string | null
  name: string
  logoUrl: string | null
  primaryColor: string | null
  event: TvActivityEvent
  /** Amount in dollars (per billing period for paid/renewed, one-time for topup), else null */
  amount: number | null
  /** Billing cadence of the amount; null for topup/trial/churned */
  cadence: 'monthly' | 'yearly' | null
  /** ISO timestamp of the event */
  timestamp: string
}

export interface TvActivityFeedResponse {
  items: TvActivityItem[]
}

export const tvApi = {
  async getMetrics(): Promise<TvMetricsResponse> {
    return birdbrainApiFetch<TvMetricsResponse>('/v1/birdbrain/tv')
  },

  async getMetricsWithApiKey(apiKey: string): Promise<TvMetricsResponse> {
    return apiKeyFetch<TvMetricsResponse>('/v1/birdbrain/tv', apiKey)
  },

  async getActivityFeed(limit = 40): Promise<TvActivityFeedResponse> {
    return birdbrainApiFetch<TvActivityFeedResponse>(
      `/v1/birdbrain/tv/activity-feed?limit=${limit}`
    )
  },

  async getActivityFeedWithApiKey(apiKey: string, limit = 40): Promise<TvActivityFeedResponse> {
    return apiKeyFetch<TvActivityFeedResponse>(
      `/v1/birdbrain/tv/activity-feed?limit=${limit}`,
      apiKey
    )
  },
}

// ============================================
// Marketing API (from birdbrain-api / BigQuery)
// ============================================

export interface MarketingCampaign {
  channel: string
  campaignId: string
  campaign: string
  /** Null = no ad platform data (UTM-only). 0 = campaign had $0 spend. */
  cost: number | null
  /** Null = no ad platform data (UTM-only). 0 = no impressions. */
  impressions: number | null
  /** Null = no ad platform data (UTM-only). 0 = no clicks. */
  clicks: number | null
  reach?: number
  costPerClick?: number
  clickThroughRate?: number
  costPerMille?: number
}

export interface MarketingWeekData {
  weekNumber: number
  weekLabel: string
  dateRange: string
  startDate: string
  endDate: string
  isCurrentWeek: boolean
  channels: MarketingCampaign[]
  totalCost: number
  totalImpressions: number
  totalClicks: number
  totalReach: number
}

export interface MarketingMetricsResponse {
  weeks: MarketingWeekData[]
  timestamp: string
}

export interface FetchMarketingMetricsParams {
  weeksBack?: number
}

export const marketingApi = {
  async getMarketingMetrics(
    params: FetchMarketingMetricsParams = {}
  ): Promise<MarketingMetricsResponse> {
    const queryParams = new URLSearchParams()
    if (params.weeksBack) queryParams.set('weeksBack', params.weeksBack.toString())

    const queryString = queryParams.toString()
    const endpoint = `/v1/birdbrain/marketing/metrics${queryString ? `?${queryString}` : ''}`
    return birdbrainApiFetch<MarketingMetricsResponse>(endpoint)
  },

  async getMarketingMetricsWithApiKey(
    apiKey: string,
    params: FetchMarketingMetricsParams = {}
  ): Promise<MarketingMetricsResponse> {
    const queryParams = new URLSearchParams()
    if (params.weeksBack) queryParams.set('weeksBack', params.weeksBack.toString())

    const queryString = queryParams.toString()
    const endpoint = `/v1/birdbrain/marketing/metrics${queryString ? `?${queryString}` : ''}`
    return apiKeyFetch<MarketingMetricsResponse>(endpoint, apiKey)
  },
}

// ============================================
// Engagement API (Engagement 2.0: content engine first)
// Subscriber-scoped like Retention, but defaults to trial+paid because
// trial engagement IS activation.
// ============================================

export const ENGAGEMENT_PLATFORMS = [
  'all',
  'instagram',
  'facebook',
  'linkedin',
  'pinterest',
  'tiktok',
  'youtube',
  'twitter',
] as const
export type EngagementPlatform = (typeof ENGAGEMENT_PLATFORMS)[number]

export const ENGAGEMENT_ORIGINS = ['all', 'autopilot', 'manual'] as const
export type EngagementOrigin = (typeof ENGAGEMENT_ORIGINS)[number]

export const ENGAGEMENT_BREAKDOWN_DIMENSIONS = ['platform', 'shape', 'source', 'gateway'] as const
export type EngagementBreakdownDimension = (typeof ENGAGEMENT_BREAKDOWN_DIMENSIONS)[number]

export const ENGAGEMENT_EVENT_TYPES = [
  'scheduled',
  'publish_success',
  'publish_failed',
  'approved',
  'needs_approval',
  'post_deleted',
  'composer_message',
  'channel_connected',
  'plan_created',
] as const
export type EngagementEventType = (typeof ENGAGEMENT_EVENT_TYPES)[number]

export type EngagementEventSortField = 'time' | 'name' | 'email' | 'platform'

export const ENGAGEMENT_FEATURES = ['chat', 'publish', 'queue', 'approved', 'connect'] as const
export type EngagementFeature = (typeof ENGAGEMENT_FEATURES)[number]

export type FeatureUserSortField = 'lastActive' | 'actions' | 'name' | 'email'

export interface PipelineWeek {
  weekStart: string
  isCurrentWeek: boolean
  scheduled: number
  published: number
  failed: number
  successRate: number | null
  publishedAutopilot: number
  publishedManual: number
}

export interface EngagementPipelineResponse {
  weeks: PipelineWeek[]
  basis: RetentionBasis
  platform: EngagementPlatform
  origin: EngagementOrigin
  weeksBack: number
  scheduledDataSince: string
  timestamp: string
}

export interface EngagementBreakdownRow {
  name: string
  scheduled: number | null
  published: number
  failed: number
  successRate: number | null
}

export interface EngagementBreakdownResponse {
  rows: EngagementBreakdownRow[]
  by: EngagementBreakdownDimension
  weekStart: string
  basis: RetentionBasis
  timestamp: string
}

export interface PublishHealthCell {
  platform: string
  gateway: string
  success: number
  failed: number
  successRate: number | null
}

export interface PublishHealthResponse {
  cells: PublishHealthCell[]
  totals: { success: number; failed: number; successRate: number | null }
  weeksBack: number
  basis: RetentionBasis
  timestamp: string
}

export interface EngagementEvent {
  eventId: string
  eventType: EngagementEventType
  timestamp: string
  userId: string
  email: string | null
  name: string | null
  businessId: string | null
  businessName: string | null
  platform: string | null
  platforms: string[]
  shape: string | null
  mediaType: string | null
  source: string | null
  gateway: string | null
  origin: string | null
  failureReason: string | null
  error: string | null
  creativeId: string | null
}

export interface EngagementEventsResponse {
  events: EngagementEvent[]
  totalCount: number
  hasMore: boolean
  timestamp: string
}

export interface FeatureWeekCell {
  weekStart: string
  users: number
  denominator: number
  pct: number
  isCurrentWeek: boolean
}

export interface FeatureRow {
  feature: EngagementFeature
  weeks: FeatureWeekCell[]
}

export interface EngagementFeaturesResponse {
  features: FeatureRow[]
  weeksBack: number
  basis: RetentionBasis
  timestamp: string
}

export interface FeatureUser {
  userId: string
  email: string | null
  name: string | null
  planTier: string | null
  channel: string | null
  actionsInWeek: number
  lastActionAt: string | null
  activeDaysInWeek: number
  inWeeklyActive: boolean
}

export interface FeatureUsersResponse {
  users: FeatureUser[]
  denominator: number
  totalCount: number
  hasMore: boolean
  timestamp: string
}

export interface AutopilotWeek {
  weekStart: string
  isCurrentWeek: boolean
  plansCreated: number
  plansReview: number
  plansAutomatic: number
  wizardUsers: number
  wizardAbandoned: number
  queueUsers: number
  queueViews: number
  approvals: number
}

export interface WizardStep {
  step: string
  stepIndex: number | null
  users: number
}

export interface EngagementAutopilotResponse {
  weeks: AutopilotWeek[]
  wizardSteps: WizardStep[]
  dataSince: string
  weeksBack: number
  basis: RetentionBasis
  timestamp: string
}

export interface LeaderboardRow {
  businessId: string | null
  businessName: string | null
  userId: string
  email: string | null
  name: string | null
  publishes: number
  scheduled: number
  approvals: number
  composerMessages: number
  activeDays: number
  lastPublishAt: string | null
}

export interface EngagementLeaderboardResponse {
  rows: LeaderboardRow[]
  weeksBack: number
  basis: RetentionBasis
  timestamp: string
}

export interface PresenceDay {
  date: string
  dau: number
  wau: number
  mau: number
  stickiness: number | null
  messages: number
  chatters: number
}

export interface EngagementPresenceResponse {
  days: PresenceDay[]
  current: { dau: number; wau: number; mau: number; stickiness: number | null }
  daysBack: number
  basis: RetentionBasis
  timestamp: string
}

export const engagementApi = {
  async getPipeline(params: {
    weeksBack: number
    basis: RetentionBasis
    platform: EngagementPlatform
    origin: EngagementOrigin
  }): Promise<EngagementPipelineResponse> {
    return birdbrainApiFetch<EngagementPipelineResponse>(
      `/v1/birdbrain/metrics/engagement/pipeline${retentionQuery(params)}`
    )
  },

  async getBreakdown(params: {
    weekStart: string
    by: EngagementBreakdownDimension
    basis: RetentionBasis
    platform: EngagementPlatform
    origin: EngagementOrigin
  }): Promise<EngagementBreakdownResponse> {
    return birdbrainApiFetch<EngagementBreakdownResponse>(
      `/v1/birdbrain/metrics/engagement/breakdown${retentionQuery(params)}`
    )
  },

  async getPublishHealth(params: {
    weeksBack: number
    basis: RetentionBasis
  }): Promise<PublishHealthResponse> {
    return birdbrainApiFetch<PublishHealthResponse>(
      `/v1/birdbrain/metrics/engagement/publish-health${retentionQuery(params)}`
    )
  },

  async getEvents(params: {
    weekStart?: string
    weeksBack?: number
    types?: string
    basis: RetentionBasis
    platform?: EngagementPlatform
    shape?: string
    source?: string
    gateway?: string
    origin?: EngagementOrigin
    search?: string
    sortBy: EngagementEventSortField
    sortOrder: 'asc' | 'desc'
    limit: number
    offset: number
  }): Promise<EngagementEventsResponse> {
    return birdbrainApiFetch<EngagementEventsResponse>(
      `/v1/birdbrain/metrics/engagement/events${retentionQuery(params)}`
    )
  },

  async getFeatures(params: {
    weeksBack: number
    basis: RetentionBasis
  }): Promise<EngagementFeaturesResponse> {
    return birdbrainApiFetch<EngagementFeaturesResponse>(
      `/v1/birdbrain/metrics/engagement/features${retentionQuery(params)}`
    )
  },

  async getFeatureUsers(params: {
    weekStart: string
    feature: EngagementFeature
    did: 'did' | 'didnt'
    basis: RetentionBasis
    search?: string
    sortBy: FeatureUserSortField
    sortOrder: 'asc' | 'desc'
    limit: number
    offset: number
  }): Promise<FeatureUsersResponse> {
    return birdbrainApiFetch<FeatureUsersResponse>(
      `/v1/birdbrain/metrics/engagement/feature-users${retentionQuery(params)}`
    )
  },

  async getAutopilot(params: {
    weeksBack: number
    basis: RetentionBasis
  }): Promise<EngagementAutopilotResponse> {
    return birdbrainApiFetch<EngagementAutopilotResponse>(
      `/v1/birdbrain/metrics/engagement/autopilot${retentionQuery(params)}`
    )
  },

  async getLeaderboard(params: {
    weeksBack: number
    basis: RetentionBasis
  }): Promise<EngagementLeaderboardResponse> {
    return birdbrainApiFetch<EngagementLeaderboardResponse>(
      `/v1/birdbrain/metrics/engagement/leaderboard${retentionQuery(params)}`
    )
  },

  async getPresence(params: {
    daysBack: number
    basis: RetentionBasis
  }): Promise<EngagementPresenceResponse> {
    return birdbrainApiFetch<EngagementPresenceResponse>(
      `/v1/birdbrain/metrics/engagement/presence${retentionQuery(params)}`
    )
  },
}

// ============================================
// Retention API (Retention 2.0: subscriber-scoped)
// Taka has a hard paywall, so retention is measured over subscribed users
// (trial or paid); there is deliberately no "all users" audience.
// ============================================

export const RETENTION_BASES = ['subscribed', 'paid'] as const
export type RetentionBasis = (typeof RETENTION_BASES)[number]

export const RETENTION_PLATFORMS = ['all', 'ios', 'android', 'web', 'unknown'] as const
export type RetentionPlatform = (typeof RETENTION_PLATFORMS)[number]

export const RETENTION_BREAKDOWN_DIMENSIONS = ['channel', 'platform', 'campaign'] as const
export type RetentionBreakdownDimension = (typeof RETENTION_BREAKDOWN_DIMENSIONS)[number]

export const RETENTION_ACTIVITY_FILTERS = ['all', 'active', 'inactive'] as const
export type RetentionActivityFilter = (typeof RETENTION_ACTIVITY_FILTERS)[number]

export type RetentionUserSortField = 'lastActive' | 'activeDays' | 'name' | 'email' | 'cohortDate'

export const SUBSCRIPTION_STATES = ['trialing', 'active', 'at_risk', 'churned'] as const
export type SubscriptionState = (typeof SUBSCRIPTION_STATES)[number]

export const SURVIVAL_BILLING_PERIODS = ['all', 'Monthly', 'Annual'] as const
export type SurvivalBillingPeriod = (typeof SURVIVAL_BILLING_PERIODS)[number]

export const SURVIVAL_PLAN_TIERS = ['all', 'Pro', 'Business'] as const
export type SurvivalPlanTier = (typeof SURVIVAL_PLAN_TIERS)[number]

export const SURVIVAL_BREAKDOWN_DIMENSIONS = [
  'planTier',
  'channel',
  'platform',
  'campaign',
] as const
export type SurvivalBreakdownDimension = (typeof SURVIVAL_BREAKDOWN_DIMENSIONS)[number]

export const SURVIVAL_RETAINED_FILTERS = ['all', 'retained', 'churned'] as const
export type SurvivalRetainedFilter = (typeof SURVIVAL_RETAINED_FILTERS)[number]

export type SurvivalUserSortField = 'paidStart' | 'monthsCovered' | 'name' | 'email'

export const CHURN_EVENT_TYPES = [
  'cancellation',
  'uncancellation',
  'expiration',
  'trial_cancelled',
  'billing_issue',
] as const
export type ChurnEventType = (typeof CHURN_EVENT_TYPES)[number]

export const LIFECYCLE_STAGES = ['new', 'active', 'resurrected', 'at_risk', 'zombie'] as const
export type RetentionLifecycleStage = (typeof LIFECYCLE_STAGES)[number]

export interface RetentionKpis {
  w1Rate: number | null
  w1Change: number | null
  w4Rate: number | null
  w4Change: number | null
}

export interface RetentionCohortRow {
  cohortWeekStart: string
  cohortSize: number
  weeksSinceCohort: number
  isCurrentWeek: boolean
  weeks: number[]
  retainedCounts: number[]
}

export interface RetentionCohortsResponse {
  kpis: RetentionKpis
  cohorts: RetentionCohortRow[]
  basis: RetentionBasis
  channel: TroiChannel
  platform: RetentionPlatform
  weeksBack: number
  maxWeeks: number
  timestamp: string
}

export interface RetentionBreakdownRow {
  name: string
  cohortSize: number
  weeks: number[]
  retainedCounts: number[]
}

export interface RetentionBreakdownResponse {
  rows: RetentionBreakdownRow[]
  by: RetentionBreakdownDimension
  cohortWeekStart: string
  basis: RetentionBasis
  weeksSinceCohort: number
  timestamp: string
}

export interface RetentionUser {
  userId: string
  email: string | null
  name: string | null
  channel: TroiChannel
  campaign: string | null
  platform: string
  planTier: string | null
  billingPeriod: string | null
  trialStarted: number
  converted: number
  cohortDate: string
  activeDaysInWeek: number
  lastActiveDate: string | null
  totalActiveDays: number
  subscriptionState: SubscriptionState
}

export interface RetentionCohortUsersResponse {
  users: RetentionUser[]
  totalCount: number
  hasMore: boolean
  timestamp: string
}

export interface SurvivalKpis {
  renewalRate: number | null
  renewalRateChange: number | null
}

export interface SurvivalCohortRow {
  cohortMonth: string
  cohortSize: number
  monthsSinceCohort: number
  months: number[]
  retainedCounts: number[]
  /** % still paying now (coverage reaching the mart's latest build), not the newest cell */
  nowPct: number
  nowCount: number
}

export interface RetentionSurvivalResponse {
  kpis: SurvivalKpis
  cohorts: SurvivalCohortRow[]
  billingPeriod: SurvivalBillingPeriod
  planTier: SurvivalPlanTier
  channel: TroiChannel
  monthsBack: number
  maxMonths: number
  timestamp: string
}

export interface SurvivalBreakdownRow {
  name: string
  cohortSize: number
  months: number[]
  retainedCounts: number[]
}

export interface SurvivalBreakdownResponse {
  rows: SurvivalBreakdownRow[]
  by: SurvivalBreakdownDimension
  cohortMonth: string
  monthsSinceCohort: number
  timestamp: string
}

export interface SurvivalUser {
  userId: string
  email: string | null
  name: string | null
  planTier: string
  billingPeriod: string
  channel: TroiChannel
  campaign: string | null
  platform: string
  paidStartDate: string
  monthsCovered: number
  lastCoveredOffset: number
  coveredAtOffset: number
  state: 'retained' | 'at_risk' | 'churned'
}

export interface SurvivalUsersResponse {
  users: SurvivalUser[]
  totalCount: number
  hasMore: boolean
  timestamp: string
}

export interface ChurnEvent {
  eventId: string
  userId: string
  email: string | null
  name: string | null
  eventType: ChurnEventType
  eventDate: string
  planTier: string
  billingPeriod: string
  isTrial: boolean
  cancelReason: string | null
  expirationReason: string | null
  refundAmount: number | null
  periodsSurvived: number
  daysSinceFirstPaid: number | null
  lifetimeRevenue: number
  expirationAt: string | null
  channel: TroiChannel
  campaign: string | null
  platform: string
}

export interface AtRiskUser {
  userId: string
  email: string | null
  name: string | null
  planTier: string
  billingPeriod: string
  channel: TroiChannel
  cancelledAt: string
  cancelReason: string | null
  expiresAt: string
  daysUntilExpiration: number
  lifetimeRevenue: number
}

export interface ChurnKpis {
  churnedThisMonth: number
  churnedPrevMonth: number
  churnedThisWeek: number
  atRiskCount: number
  activeSubscriptions: number | null
  activeSubscriptionsPrev: number | null
  activeTrials: number | null
}

export interface RetentionChurnResponse {
  kpis: ChurnKpis
  /** Total events in the window; events[] is capped at 500 */
  totalEvents: number
  events: ChurnEvent[]
  atRisk: AtRiskUser[]
  daysBack: number
  timestamp: string
}

export interface RetentionLifecycleStageCount {
  stage: RetentionLifecycleStage
  label: string
  count: number
  percentage: number
}

export interface RetentionLifecycleResponse {
  stages: RetentionLifecycleStageCount[]
  totalSubscribers: number
  timestamp: string
}

export interface RetentionLifecycleUser {
  userId: string
  email: string | null
  name: string | null
  planTier: string | null
  billingPeriod: string | null
  trialingNow: boolean
  subscribedSince: string
  lastActiveDate: string | null
  activeDays28d: number
}

export interface RetentionLifecycleUsersResponse {
  users: RetentionLifecycleUser[]
  stage: RetentionLifecycleStage
  totalCount: number
  hasMore: boolean
  timestamp: string
}

function retentionQuery(
  params: Record<string, string | number | boolean | undefined | null>
): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    qs.set(key, String(value))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export const retentionApi = {
  async getRetentionCohorts(params: {
    weeksBack: number
    basis: RetentionBasis
    channel: TroiChannel
    platform: RetentionPlatform
  }): Promise<RetentionCohortsResponse> {
    return birdbrainApiFetch<RetentionCohortsResponse>(
      `/v1/birdbrain/metrics/retention/cohorts${retentionQuery(params)}`
    )
  },

  async getRetentionBreakdown(params: {
    cohortWeekStart: string
    by: RetentionBreakdownDimension
    basis: RetentionBasis
    channel: TroiChannel
    platform: RetentionPlatform
  }): Promise<RetentionBreakdownResponse> {
    return birdbrainApiFetch<RetentionBreakdownResponse>(
      `/v1/birdbrain/metrics/retention/breakdown${retentionQuery(params)}`
    )
  },

  async getRetentionCohortUsers(params: {
    cohortWeekStart: string
    weekOffset?: number
    activity: RetentionActivityFilter
    basis: RetentionBasis
    channel: TroiChannel
    platform: RetentionPlatform
    campaign?: string
    search?: string
    sortBy: RetentionUserSortField
    sortOrder: 'asc' | 'desc'
    limit: number
    offset: number
  }): Promise<RetentionCohortUsersResponse> {
    return birdbrainApiFetch<RetentionCohortUsersResponse>(
      `/v1/birdbrain/metrics/retention/cohort-users${retentionQuery(params)}`
    )
  },

  async getRetentionSurvival(params: {
    monthsBack: number
    billingPeriod: SurvivalBillingPeriod
    planTier: SurvivalPlanTier
    channel: TroiChannel
  }): Promise<RetentionSurvivalResponse> {
    return birdbrainApiFetch<RetentionSurvivalResponse>(
      `/v1/birdbrain/metrics/retention/survival${retentionQuery(params)}`
    )
  },

  async getSurvivalBreakdown(params: {
    cohortMonth: string
    by: SurvivalBreakdownDimension
    billingPeriod: SurvivalBillingPeriod
    planTier: SurvivalPlanTier
    channel: TroiChannel
  }): Promise<SurvivalBreakdownResponse> {
    return birdbrainApiFetch<SurvivalBreakdownResponse>(
      `/v1/birdbrain/metrics/retention/survival/breakdown${retentionQuery(params)}`
    )
  },

  async getSurvivalUsers(params: {
    cohortMonth: string
    monthOffset?: number
    retained: SurvivalRetainedFilter
    billingPeriod: SurvivalBillingPeriod
    planTier: SurvivalPlanTier
    channel: TroiChannel
    platform?: RetentionPlatform
    campaign?: string
    search?: string
    sortBy: SurvivalUserSortField
    sortOrder: 'asc' | 'desc'
    limit: number
    offset: number
  }): Promise<SurvivalUsersResponse> {
    return birdbrainApiFetch<SurvivalUsersResponse>(
      `/v1/birdbrain/metrics/retention/survival/users${retentionQuery(params)}`
    )
  },

  async getRetentionChurn(daysBack: number): Promise<RetentionChurnResponse> {
    return birdbrainApiFetch<RetentionChurnResponse>(
      `/v1/birdbrain/metrics/retention/churn${retentionQuery({ daysBack })}`
    )
  },

  async getRetentionLifecycle(): Promise<RetentionLifecycleResponse> {
    return birdbrainApiFetch<RetentionLifecycleResponse>(
      '/v1/birdbrain/metrics/retention/lifecycle'
    )
  },

  async getRetentionLifecycleUsers(params: {
    stage: RetentionLifecycleStage
    search?: string
    limit: number
    offset: number
  }): Promise<RetentionLifecycleUsersResponse> {
    return birdbrainApiFetch<RetentionLifecycleUsersResponse>(
      `/v1/birdbrain/metrics/retention/lifecycle/users${retentionQuery(params)}`
    )
  },
}

// ============================================
// TROI API (Time to Return on Investment)
// ============================================

export const TROI_CHANNELS = ['all', 'facebook', 'google', 'apple', 'unattributed'] as const
export type TroiChannel = (typeof TROI_CHANNELS)[number]

export const TROI_GRANULARITIES = ['week', 'month', 'quarter'] as const
export type TroiGranularity = (typeof TROI_GRANULARITIES)[number]

export interface TroiCohortRow {
  cohortPeriod: string
  cohortLabel: string
  channel: TroiChannel
  signups: number
  paidConversions: number
  marketingSpend: number
  totalRevenue: number
  cac: number | null
  annualPayers: number
  monthlyPayers: number
  yearlyShare: number | null
  periodsSinceCohort: number
  periods: number[]
  projectedPeriods: number[]
  projectedPaybackMonth: number | null
  projectionEligible: boolean
}

export interface TroiMetricsResponse {
  cohorts: TroiCohortRow[]
  includeTopups: boolean
  channel: TroiChannel
  appliedCampaigns: string[]
  granularity: TroiGranularity
  maxPeriods: number
  projectionAvailable: boolean
  timestamp: string
}

export const TROI_TRANSACTION_TYPES = [
  'initial_purchase',
  'trial_conversion',
  'renewal',
  'refund',
  'topup',
] as const
export type TroiTransactionType = (typeof TROI_TRANSACTION_TYPES)[number]

export const TROI_BREAKDOWN_DIMENSIONS = ['channel', 'campaign'] as const
export type TroiBreakdownDimension = (typeof TROI_BREAKDOWN_DIMENSIONS)[number]

export interface TroiTransaction {
  userId: string
  email: string | null
  name: string | null
  channel: TroiChannel
  campaign: string | null
  planTier: string | null
  billingPeriod: string | null
  eventDate: string
  periodOffset: number
  eventType: TroiTransactionType
  txBillingPeriod: string | null
  revenue: number
}

export interface TroiTransactionsResponse {
  transactions: TroiTransaction[]
  cohortPeriod: string
  granularity: TroiGranularity
  channel: TroiChannel
  appliedCampaigns: string[]
  timestamp: string
}

export interface TroiBreakdownRow {
  name: string
  channel: TroiChannel
  signups: number
  paidConversions: number
  marketingSpend: number
  totalRevenue: number
  cac: number | null
  periods: number[]
  paybackPeriod: number | null
}

export interface TroiBreakdownResponse {
  rows: TroiBreakdownRow[]
  by: TroiBreakdownDimension
  cohortPeriod: string
  granularity: TroiGranularity
  includeTopups: boolean
  maxPeriods: number
  timestamp: string
}

export interface TroiCampaign {
  campaign: string
  channel: TroiChannel
  lifetimeSpend: number
}

export interface TroiCampaignsResponse {
  campaigns: TroiCampaign[]
  granularity: TroiGranularity
  timestamp: string
}

export const troiApi = {
  async getTroiMetrics(
    periodsBack?: number,
    channel?: TroiChannel,
    granularity?: TroiGranularity,
    campaigns?: string[],
    includeTopups?: boolean
  ): Promise<TroiMetricsResponse> {
    const search = new URLSearchParams()
    if (periodsBack) search.set('periodsBack', String(periodsBack))
    if (channel) search.set('channel', channel)
    if (granularity) search.set('granularity', granularity)
    // Repeated params (campaigns=a&campaigns=b) so campaign names containing
    // commas survive the round-trip.
    if (campaigns) for (const c of campaigns) search.append('campaigns', c)
    if (includeTopups === false) search.set('includeTopups', '0')
    const qs = search.toString()
    const suffix = qs ? `?${qs}` : ''
    return birdbrainApiFetch<TroiMetricsResponse>(`/v1/birdbrain/metrics/troi${suffix}`)
  },

  async getTroiCampaigns(granularity?: TroiGranularity): Promise<TroiCampaignsResponse> {
    const search = new URLSearchParams()
    if (granularity) search.set('granularity', granularity)
    const qs = search.toString()
    const suffix = qs ? `?${qs}` : ''
    return birdbrainApiFetch<TroiCampaignsResponse>(`/v1/birdbrain/metrics/troi/campaigns${suffix}`)
  },

  async getTroiTransactions(params: {
    cohortPeriod: string
    granularity: TroiGranularity
    channel?: TroiChannel
    campaigns?: string[]
  }): Promise<TroiTransactionsResponse> {
    const search = new URLSearchParams({
      cohortPeriod: params.cohortPeriod,
      granularity: params.granularity,
    })
    if (params.channel && params.channel !== 'all') search.set('channel', params.channel)
    if (params.campaigns) for (const c of params.campaigns) search.append('campaigns', c)
    return birdbrainApiFetch<TroiTransactionsResponse>(
      `/v1/birdbrain/metrics/troi/transactions?${search}`
    )
  },

  async getTroiBreakdown(params: {
    cohortPeriod: string
    granularity: TroiGranularity
    by: TroiBreakdownDimension
    channel?: TroiChannel
    campaigns?: string[]
    includeTopups?: boolean
  }): Promise<TroiBreakdownResponse> {
    const search = new URLSearchParams({
      cohortPeriod: params.cohortPeriod,
      granularity: params.granularity,
      by: params.by,
    })
    if (params.channel && params.channel !== 'all') search.set('channel', params.channel)
    if (params.campaigns) for (const c of params.campaigns) search.append('campaigns', c)
    if (params.includeTopups === false) search.set('includeTopups', '0')
    return birdbrainApiFetch<TroiBreakdownResponse>(
      `/v1/birdbrain/metrics/troi/breakdown?${search}`
    )
  },
}

// ============================================
// Campaigns API (from birdbrain-api / BigQuery)
// ============================================

export interface CampaignOverview {
  id: string
  campaignId: string
  campaignName: string
  platform: string
  platformDisplay: string
  firstActivityDate?: string
  lastActivityDate?: string
  inferredStatus: string
  // Lifetime metrics (always available)
  lifetimeCost: number
  lifetimeImpressions: number
  lifetimeClicks: number
  lifetimeReach?: number
  lifetimeCpc?: number
  lifetimeCtr?: number
  // Pre-computed period metrics (for backwards compat)
  costLast7d: number
  impressionsLast7d: number
  clicksLast7d: number
  ctrLast7d?: number
  costLast30d: number
  // Entity counts
  adsetCount: number
  adCount: number
  daysActive: number
  objective?: string
  // Configuration fields
  channelType?: string
  channelSubType?: string
  biddingStrategy?: string
  // Period-specific metrics (based on selected period: 7d, 28d, or lifetime)
  periodCost: number
  periodImpressions: number
  periodClicks: number
  periodCtr?: number
  // Funnel metrics (cohorted to selected period)
  lpViews?: number
  lpCtaClicked?: number
  appOpened?: number
  signedUp?: number
  onboarded?: number
  activated?: number
  trialStarted?: number
  converted?: number
  subscribed?: number
  // Acquisition cost metrics (for selected period)
  cpaSignup?: number
  cptTrial?: number
  cacPaying?: number
  cacSubscribe?: number
  // Landing page count (derived from ad creatives)
  lpCount?: number
  // Lifetime ROAS (cohort-attributed, from the per-campaign TROI marts)
  roas?: number
  roasRevenue?: number
  roasSpend?: number
  roasCohortAgeMonths?: number
}

export interface CampaignDaily {
  reportDate: string
  weekStart: string
  id: string
  campaignName: string
  platform: string
  cost: number
  impressions: number
  clicks: number
  ctr?: number
  cumulativeCost: number
  cost7dAvg: number
  ctr7dAvg: number
}

export interface CampaignHierarchyItem {
  level: 'campaign' | 'adset' | 'ad'
  id: string
  entityId: string
  entityName: string
  parentId?: string
  parentName?: string
  campaignId: string
  campaignName: string
  platform: string
  platformDisplay: string
  inferredStatus: string
  cost: number
  impressions: number
  clicks: number
  reach?: number
  cpc?: number
  ctr?: number
  costLast7d: number
  impressionsLast7d: number
  clicksLast7d: number
  shareOfParentCost?: number
  childCount: number
  sortOrder: number
  children?: CampaignHierarchyItem[]
}

export interface PlatformSummary {
  platform: string
  platformDisplay: string
  campaignCount: number
  activeCampaignCount: number
  totalCost: number
  totalImpressions: number
  totalClicks: number
  avgCtr?: number
  costLast7d: number
  costLast30d: number
}

export interface CampaignsListResponse {
  campaigns: CampaignOverview[]
  platformSummary: PlatformSummary[]
  totalCount: number
  timestamp: string
}

/**
 * Facebook/Instagram ad creative (visual ads with images, videos, Instagram previews)
 */
export interface FacebookAdCreative {
  id: string
  adId: string
  adName?: string
  /** Thumbnail URL - 9:16 aspect ratio (1080x1920) */
  thumbnailUrl9_16?: string
  /** Thumbnail URL - 1:1 aspect ratio (1080x1080) */
  thumbnailUrl1_1?: string
  imageUrl?: string
  headline?: string
  body?: string
  ctaType?: string
  /** Instagram permalink to view actual ad with video playback */
  previewUrl?: string
  isVideo: boolean
  /** Facebook video URL for "Play on Facebook" link */
  videoUrl?: string
  landingPageUrl?: string
  platform: 'facebook'
}

/**
 * Google Ads RSA (Responsive Search Ad) creative
 */
export interface GoogleAdCreative {
  id: string
  adId: string
  adName?: string
  adType?: string
  thumbnailUrl?: string
  /** Primary headline (first headline for display) */
  primaryHeadline?: string
  /** Primary description (first description for display) */
  primaryDescription?: string
  /** All RSA headlines (Google dynamically combines 3) */
  allHeadlines: string[]
  /** All RSA descriptions (Google dynamically combines 2) */
  allDescriptions: string[]
  headlineCount: number
  descriptionCount: number
  /** Landing page URL */
  landingPageUrl?: string
  platform: 'google'
}

/**
 * Union type for ad creatives - use type guards to differentiate
 */
export type AdCreative = FacebookAdCreative | GoogleAdCreative

/**
 * Type guard to check if creative is a Facebook ad
 */
export function isFacebookAdCreative(creative: AdCreative): creative is FacebookAdCreative {
  return creative.platform === 'facebook'
}

/**
 * Type guard to check if creative is a Google ad
 */
export function isGoogleAdCreative(creative: AdCreative): creative is GoogleAdCreative {
  return creative.platform === 'google'
}

export interface CampaignDetailResponse {
  campaign: CampaignOverview
  dailyMetrics: CampaignDaily[]
  hierarchy: CampaignHierarchyItem[]
  adCreatives?: AdCreative[]
  timestamp: string
}

export interface CampaignComparisonResponse {
  platforms: PlatformSummary[]
  topCampaigns: CampaignOverview[]
  timestamp: string
}

export type CampaignPeriod = '7d' | '28d' | 'lifetime'

export interface FetchCampaignsParams {
  platform?: string
  status?: string
  period?: CampaignPeriod
  limit?: number
}

export const campaignsApi = {
  async getCampaigns(params: FetchCampaignsParams = {}): Promise<CampaignsListResponse> {
    const queryParams = new URLSearchParams()
    if (params.platform) queryParams.set('platform', params.platform)
    if (params.status) queryParams.set('status', params.status)
    if (params.period) queryParams.set('period', params.period)
    if (params.limit) queryParams.set('limit', params.limit.toString())

    const queryString = queryParams.toString()
    const endpoint = `/v1/birdbrain/campaigns${queryString ? `?${queryString}` : ''}`
    return birdbrainApiFetch<CampaignsListResponse>(endpoint)
  },

  async getCampaignDetail(id: string): Promise<CampaignDetailResponse> {
    return birdbrainApiFetch<CampaignDetailResponse>(`/v1/birdbrain/campaigns/${id}`)
  },

  async getCampaignHierarchy(id: string): Promise<CampaignHierarchyItem[]> {
    return birdbrainApiFetch<CampaignHierarchyItem[]>(`/v1/birdbrain/campaigns/${id}/hierarchy`)
  },

  async getComparison(): Promise<CampaignComparisonResponse> {
    return birdbrainApiFetch<CampaignComparisonResponse>('/v1/birdbrain/campaigns/comparison')
  },
}

export type ActivityPeriod = '7d' | '30d' | '90d' | 'all'

// ============================================
// Taka Radio API (WhatsApp Broadcast Management)
// ============================================

export interface BroadcastSummary {
  id: string
  name: string
  description: string
  schedule: 'daily' | 'weekly'
  /** Human label from the server's code-defined schedule, e.g. "Daily 11:00" (Israel time) */
  scheduleLabel?: string
  recipientCount: number
  lastSentAt?: string
  lastSuccess?: boolean
}

export interface BroadcastRecipient {
  id: string
  phoneNumber: string
  name?: string
  active: boolean
  createdAt?: string
}

export interface BroadcastHistoryEntry {
  id: string
  sentAt: string
  recipientCount: number
  success: boolean
  errorMessage?: string
  renderedMessage?: string
  broadcastId?: string
  broadcastName?: string
}

export interface BroadcastDetail {
  id: string
  name: string
  description: string
  schedule: 'daily' | 'weekly'
  /** Human label from the server's code-defined schedule, e.g. "Daily 11:00" (Israel time) */
  scheduleLabel?: string
  recipientCount: number
  lastSentAt?: string
  lastSuccess?: boolean
  recipients: BroadcastRecipient[]
  recentHistory: BroadcastHistoryEntry[]
}

export interface BroadcastPreview {
  broadcastId: string
  messagePreview: string
  generatedAt: string
}

export interface ExecuteBroadcastResult {
  success: boolean
  broadcastId: string
  broadcastName: string
  recipientCount: number
  sentCount: number
  failedCount: number
  message: string
  error?: string
}

export interface AddRecipientParams {
  broadcastId: string
  phoneNumber: string
  name?: string
}

export interface UpdateRecipientParams {
  phoneNumber?: string
  name?: string
  active?: boolean
}

export const takaRadioApi = {
  // Broadcasts
  async listBroadcasts(): Promise<BroadcastSummary[]> {
    return birdbrainApiFetch<BroadcastSummary[]>('/v1/birdbrain/taka-news/broadcasts')
  },

  async getBroadcastDetail(id: string): Promise<BroadcastDetail> {
    return birdbrainApiFetch<BroadcastDetail>(`/v1/birdbrain/taka-news/broadcasts/${id}`)
  },

  /** Lazy: runs the metrics fan-out server-side, so only call when the operator asks. */
  async getBroadcastPreview(id: string): Promise<BroadcastPreview> {
    return birdbrainApiFetch<BroadcastPreview>(`/v1/birdbrain/taka-news/broadcasts/${id}/preview`)
  },

  async executeBroadcast(id: string): Promise<ExecuteBroadcastResult> {
    return birdbrainApiFetch<ExecuteBroadcastResult>(
      `/v1/birdbrain/taka-news/broadcasts/${id}/execute`,
      { method: 'POST' }
    )
  },

  // Recipients
  async getRecipients(broadcastId: string): Promise<BroadcastRecipient[]> {
    return birdbrainApiFetch<BroadcastRecipient[]>(
      `/v1/birdbrain/taka-news/broadcasts/${broadcastId}/recipients`
    )
  },

  async addRecipient(params: AddRecipientParams): Promise<BroadcastRecipient> {
    return birdbrainApiFetch<BroadcastRecipient>('/v1/birdbrain/taka-news/recipients', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  },

  async updateRecipient(id: string, params: UpdateRecipientParams): Promise<BroadcastRecipient> {
    return birdbrainApiFetch<BroadcastRecipient>(`/v1/birdbrain/taka-news/recipients/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    })
  },

  async removeRecipient(id: string): Promise<void> {
    await birdbrainApiFetch<void>(`/v1/birdbrain/taka-news/recipients/${id}`, {
      method: 'DELETE',
    })
  },

  // History
  async getHistory(broadcastId?: string, limit?: number): Promise<BroadcastHistoryEntry[]> {
    const queryParams = new URLSearchParams()
    if (broadcastId) queryParams.set('broadcastId', broadcastId)
    if (limit) queryParams.set('limit', limit.toString())

    const queryString = queryParams.toString()
    const endpoint = `/v1/birdbrain/taka-news/history${queryString ? `?${queryString}` : ''}`
    return birdbrainApiFetch<BroadcastHistoryEntry[]>(endpoint)
  },

  async getHistoryEntry(id: string): Promise<BroadcastHistoryEntry> {
    return birdbrainApiFetch<BroadcastHistoryEntry>(`/v1/birdbrain/taka-news/history/${id}`)
  },
}

// ============================================
// AI Cost API (from birdbrain-api / PostgreSQL)
// ============================================

export type AiCostCoverageStatus =
  'tracked' | 'tracked_partial' | 'manual' | 'untracked' | 'pending'

export interface AiCostCoverageEntry {
  source: string
  label: string
  status: AiCostCoverageStatus
  note: string
}

export interface AiCostDay {
  date: string
  byProvider: Record<string, number>
  /** Optional so a client deployed ahead of birdbrain-api degrades gracefully */
  byProductArea?: Record<string, number>
  total: number
}

export interface AiCostTotals {
  byProvider: Record<string, number>
  byProductArea: Record<string, number>
  total: number
}

export interface AiCostDailyTrend {
  date: string
  costCents: number
  requests: number
}

export interface AiCostByUser {
  userId: string
  userName: string
  email: string
  totalCostCents: number
  totalRequests: number
  totalTokens: number
  lastRequestAt: string
}

export interface AiCostByModel {
  model: string
  costCents: number
  requests: number
  tokens: number
}

export interface AiCostByMode {
  mode: string
  costCents: number
  requests: number
  tokens: number
}

export interface AiCostMetricsResponse {
  days: AiCostDay[]
  totals30d: AiCostTotals
  coverage: AiCostCoverageEntry[]
  periodDays: number
  timestamp: string
}

export interface AiCostUsersResponse {
  users: AiCostByUser[]
  totalCount: number
}

export interface AiCostUserDetail {
  totalCostCents: number
  totalRequests: number
  totalTokens: number
  totalPromptTokens: number
  totalCompletionTokens: number
  dailyTrend: AiCostDailyTrend[]
  byModel: AiCostByModel[]
  byMode: AiCostByMode[]
}

export const aiCostApi = {
  async getAiCostMetrics(periodDays = 30): Promise<AiCostMetricsResponse> {
    const params = periodDays !== 30 ? `?periodDays=${periodDays}` : ''
    return birdbrainApiFetch<AiCostMetricsResponse>(`/v1/birdbrain/metrics/ai-cost${params}`)
  },

  async getAiCostForUser(userId: string): Promise<AiCostUserDetail> {
    return birdbrainApiFetch<AiCostUserDetail>(`/v1/birdbrain/metrics/ai-cost/user/${userId}`)
  },

  async getAiCostUsers(
    params: {
      search?: string
      limit?: number
      offset?: number
      periodDays?: number
    } = {}
  ): Promise<AiCostUsersResponse> {
    const queryParams = new URLSearchParams()
    if (params.search) queryParams.set('search', params.search)
    if (params.limit) queryParams.set('limit', params.limit.toString())
    if (params.offset != null) queryParams.set('offset', params.offset.toString())
    if (params.periodDays) queryParams.set('periodDays', params.periodDays.toString())
    const qs = queryParams.toString()
    return birdbrainApiFetch<AiCostUsersResponse>(
      `/v1/birdbrain/metrics/ai-cost/users${qs ? `?${qs}` : ''}`
    )
  },
}

// ============================================
// Session replay (PostHog): per-user replay digest + on-demand AI recap
// ============================================

export type ReplayEventKind = 'event' | 'exception'
export type ReplayEventOrigin = 'browser' | 'server'

export interface ReplaySessionEvent {
  at: string
  /** Typed event name, or "<type>: <message>" for a captured exception */
  name: string
  path: string | null
  kind: ReplayEventKind
  /** browser = inside the recording; server = a backend outcome during the session window */
  origin: ReplayEventOrigin
}

export interface ReplaySession {
  id: string
  /** Deep link to the recording in PostHog */
  url: string
  startedAt: string
  endedAt: string
  activeSeconds: number
  clickCount: number
  keypressCount: number
  consoleErrorCount: number
  firstUrl: string | null
  /** Named events + exceptions in order (head and tail kept when long) */
  events: ReplaySessionEvent[]
  eventsTruncated: boolean
}

export interface UserSessionReplay {
  /** False when birdbrain-api has no PostHog key / project id */
  configured: boolean
  /** PostHog person page on its Recordings tab */
  personUrl: string | null
  lookbackDays: number
  /** Newest first */
  sessions: ReplaySession[]
  /** Set when the PostHog lookup failed (sessions is empty then) */
  probeError: string | null
  timestamp: string
}

export interface SessionRecap {
  /** Markdown; empty string when the user has no recorded sessions */
  recap: string
  sessionsConsidered: number
  model: string
  generatedAt: string
}

export const sessionReplayApi = {
  async getUserSessions(userId: string): Promise<UserSessionReplay> {
    return birdbrainApiFetch<UserSessionReplay>(
      `/v1/birdbrain/session-replay/users/${encodeURIComponent(userId)}/sessions`
    )
  },

  async recapUserSessions(userId: string): Promise<SessionRecap> {
    return birdbrainApiFetch<SessionRecap>(
      `/v1/birdbrain/session-replay/users/${encodeURIComponent(userId)}/recap`,
      { method: 'POST' }
    )
  },
}

// ============================================
// SocialKit API (engine health, from birdbrain-api / BigQuery)
// ============================================

export interface SocialKitGenerationDaily {
  date: string
  engineId: string
  engineVersion: string
  creativeType: string
  shape: string
  totalAttempts: number
  ok: number
  notPostable: number
  insufficientCredits: number
  error: number
  fellBack: number
  scored: number
  avgScore: number | null
  p50Score: number | null
  p95Score: number | null
  totalUsdCost: number | null
  avgUsdCost: number | null
  avgDurationMs: number | null
  p50DurationMs: number | null
  p95DurationMs: number | null
  tierGreat: number
  tierStrong: number
  tierCompetent: number
  tierTemplate: number
}

export interface SocialKitGenerationSummary {
  totalAttempts: number
  ok: number
  notPostable: number
  insufficientCredits: number
  error: number
  fellBack: number
  successRate: number
  fallbackRate: number
  errorRate: number
  avgScore: number | null
  totalUsdCost: number
  avgUsdCost: number | null
  avgDurationMs: number | null
}

export interface SocialKitGenerationResponse {
  daysBack: number
  rows: SocialKitGenerationDaily[]
  summary: SocialKitGenerationSummary
  timestamp: string
}

export interface SocialKitCount {
  key: string
  count: number
}

export interface SocialKitAdminStats {
  generatedAt: number
  // Self-serve accounts only; partner-owned tenants are counted under `partners`.
  accounts: {
    total: number
    newLast7d: number
    newLast30d: number
    byPlan: SocialKitCount[]
  }
  // Partner tier (optional: absent from a Worker older than migration 0015).
  partners?: Array<{
    slug: string
    status: 'active' | 'suspended'
    tenants: number
    creditsThisPeriod: number
    lastActivityAt: number | null
  }>
  posts: {
    byStatus: SocialKitCount[]
    byPlatform: SocialKitCount[]
    publishedLast24h: number
    publishedLast7d: number
    failed: number
    recentFailures: Array<{ postId: string; platform: string; error: string; at: number }>
  }
  scheduler: {
    byStatus: SocialKitCount[]
    dueBacklog: number
    stuckClaimed: number
  }
  video: {
    byStatus: SocialKitCount[]
    runningByPhase: SocialKitCount[]
    stalledRunning: number
    oldestRunningAgeMs: number | null
    recentErrors: Array<{ jobId: string; error: string; at: number }>
  }
  metricPolls: {
    tracked: number
    parked: number
    withErrors: number
    recentErrors: Array<{ postId: string; error: string; at: number | null }>
  }
  engagement: {
    postsWithMetrics: number
    impressions: number
    likes: number
    comments: number
    shares: number
    clicks: number
  }
  scores: {
    count: number
    avg: number | null
    distribution: SocialKitCount[]
  }
}

export interface SocialKitOpsResponse {
  configured: boolean
  stats: SocialKitAdminStats | null
  error?: string
  timestamp: string
}

export interface SocialKitGenerationEvent {
  timestamp: string
  userId: string
  name: string | null
  email: string | null
  businessName: string | null
  engineVersion: string
  creativeType: string
  shape: string
  outcome: string
  fellBack: boolean
  score: number | null
  usdCost: number | null
  durationMs: number | null
  designTier: string | null
}

export interface SocialKitEventsFilter {
  date?: string
  daysBack?: number
  outcome?: string
  shape?: string
  engineVersion?: string
  platform?: string
  fellBack?: boolean
  search?: string
  orderBy?: string
  orderDir?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export interface SocialKitEventsResponse {
  events: SocialKitGenerationEvent[]
  totalCount: number
  hasMore: boolean
  filter: Record<string, unknown>
  timestamp: string
}

export const socialKitApi = {
  async getGeneration(daysBack?: number): Promise<SocialKitGenerationResponse> {
    const params = daysBack ? `?daysBack=${daysBack}` : ''
    return birdbrainApiFetch<SocialKitGenerationResponse>(
      `/v1/birdbrain/socialkit/generation${params}`
    )
  },

  async getOps(): Promise<SocialKitOpsResponse> {
    return birdbrainApiFetch<SocialKitOpsResponse>('/v1/birdbrain/socialkit/ops')
  },

  async getEvents(filter: SocialKitEventsFilter = {}): Promise<SocialKitEventsResponse> {
    const q = new URLSearchParams()
    if (filter.date) q.set('date', filter.date)
    if (filter.daysBack) q.set('daysBack', String(filter.daysBack))
    if (filter.outcome) q.set('outcome', filter.outcome)
    if (filter.shape) q.set('shape', filter.shape)
    if (filter.engineVersion) q.set('engineVersion', filter.engineVersion)
    if (filter.platform) q.set('platform', filter.platform)
    if (filter.fellBack) q.set('fellBack', 'true')
    if (filter.search) q.set('search', filter.search)
    if (filter.orderBy) q.set('orderBy', filter.orderBy)
    if (filter.orderDir) q.set('orderDir', filter.orderDir)
    if (filter.limit) q.set('limit', String(filter.limit))
    if (filter.offset) q.set('offset', String(filter.offset))
    const qs = q.toString()
    return birdbrainApiFetch<SocialKitEventsResponse>(
      `/v1/birdbrain/socialkit/events${qs ? `?${qs}` : ''}`
    )
  },
}

// ============================================
// Health API (system health summary + alerts)
// ============================================

export type HealthStatus = 'healthy' | 'warning' | 'error'
export type HealthAlertLevel = 'warning' | 'error'

export interface HealthAlert {
  id: string
  level: HealthAlertLevel
  title: string
  detail: string
  /** BirdBrain route where the operator can act */
  href: string
}

export interface HealthHeroDay {
  date: string
  signups: number
  trialsStarted: number
  paidConversions: number
  /** Null when no ad platform reported that day (never a fake $0) */
  adSpend: number | null
}

export interface HealthSummaryResponse {
  status: HealthStatus
  alerts: HealthAlert[]
  freshness: {
    status: HealthStatus
    summary: {
      totalTables: number
      healthyTables: number
      warningTables: number
      errorTables: number
    }
  }
  socialkit: {
    configured: boolean
    healthy: boolean
    stalledReels: number
    erroredReels: number
  }
  /** Last 8 complete days (today excluded), ascending; null when the metrics source is failing */
  hero: HealthHeroDay[] | null
  timestamp: string
}

export type CanaryState = 'fresh' | 'stale' | 'missing'

export interface ErrorTrackingCanary {
  service: string
  /** fresh = heartbeat within 26h; stale = capture regression; missing = awaiting first heartbeat */
  state: CanaryState
  lastSeen: string | null
  hoursSince: number | null
  issueUrl: string | null
}

export interface ErrorTrackingIssue {
  id: string
  name: string | null
  description: string | null
  firstSeen: string
  lastSeen: string
  occurrences: number
  users: number
  sessions: number
  library: string | null
  /** 7-day occurrence trend, oldest bucket first */
  volumeBuckets: number[]
  url: string
}

export interface ErrorTrackingHealthResponse {
  configured: boolean
  inboxUrl: string | null
  summary: {
    activeIssues7d: number
    newIssues24h: number
    hasMore: boolean
  } | null
  events: {
    total24h: number
    web24h: number
    server24h: number
    webUsers24h: number
    /** 7d web volume; the silent-capture alert only fires when this is > 0 */
    web7d: number
  } | null
  canaries: ErrorTrackingCanary[]
  topIssues: ErrorTrackingIssue[] | null
  probeError: string | null
  timestamp: string
}

export const healthApi = {
  async getSummary(): Promise<HealthSummaryResponse> {
    return birdbrainApiFetch<HealthSummaryResponse>('/v1/birdbrain/health/summary')
  },

  async getErrorTracking(): Promise<ErrorTrackingHealthResponse> {
    return birdbrainApiFetch<ErrorTrackingHealthResponse>('/v1/birdbrain/health/error-tracking')
  },
}

export const userActivityApi = {
  async getUserActivity(params: FetchUserActivityParams): Promise<UserActivityResponse> {
    const queryParams = new URLSearchParams()
    if (params.page) queryParams.set('page', params.page.toString())
    if (params.limit) queryParams.set('limit', params.limit.toString())
    if (params.eventName) queryParams.set('eventName', params.eventName)
    if (params.startDate) queryParams.set('startDate', params.startDate)
    if (params.endDate) queryParams.set('endDate', params.endDate)

    const queryString = queryParams.toString()
    const endpoint = `/v1/birdbrain/user-activity/${params.userId}${queryString ? `?${queryString}` : ''}`
    return birdbrainApiFetch<UserActivityResponse>(endpoint)
  },

  async getUserEventNames(userId: string): Promise<UserEventNamesResponse> {
    return birdbrainApiFetch<UserEventNamesResponse>(
      `/v1/birdbrain/user-activity/${userId}/event-names`
    )
  },

  async getDailyActivity(userId: string): Promise<DailyActivityResponse> {
    return birdbrainApiFetch<DailyActivityResponse>(
      `/v1/birdbrain/user-activity/${userId}/daily-activity`
    )
  },

  async getMostActiveUsers(
    params: { limit?: number; offset?: number; period?: ActivityPeriod } = {}
  ): Promise<MostActiveUsersResponse> {
    const queryParams = new URLSearchParams()
    if (params.limit) queryParams.set('limit', params.limit.toString())
    if (params.offset) queryParams.set('offset', params.offset.toString())
    if (params.period) queryParams.set('period', params.period)

    const queryString = queryParams.toString()
    const endpoint = `/v1/birdbrain/user-activity/most-active${queryString ? `?${queryString}` : ''}`
    return birdbrainApiFetch<MostActiveUsersResponse>(endpoint)
  },

  async getUserAttribution(userId: string): Promise<UserAttributionResponse> {
    return birdbrainApiFetch<UserAttributionResponse>(
      `/v1/birdbrain/user-activity/${userId}/attribution`
    )
  },
}

// ============================================
// Command API (audited admin actions via birdbrain-api proxy)
// ============================================

export type CommandTargetType =
  'user' | 'business' | 'engine' | 'gateway' | 'api_key' | 'client_policy' | 'promo_code'
export type CommandResultStatus = 'success' | 'failure'

/** One row of the admin audit trail (`admin_actions`). */
export interface AdminActionEntry {
  id: string
  createdAt: string
  updatedAt: string
  actorEmail: string
  /** Dot-namespaced verb: 'user.suspend' | 'user.unsuspend' | 'user.extend_trial' | 'business.grant_credits' | ... (open set) */
  action: string
  targetType: CommandTargetType
  targetId: string
  /** Human-readable snapshot of the target (email/name) at action time */
  targetLabel: string | null
  params: Record<string, unknown>
  result: CommandResultStatus
  errorMessage: string | null
  durationMs: number
}

/** Result of an executed command: the forwarded server response + audit row id. */
export interface CommandResult {
  auditId: string
  action: string
  result: CommandResultStatus
  serverStatus: number
  response: unknown
}

export interface CommandLogResponse {
  entries: AdminActionEntry[]
  totalCount: number
  hasMore: boolean
}

export interface FetchCommandLogParams {
  targetType?: CommandTargetType
  targetId?: string
  action?: string
  /** Case-insensitive substring match on the actor email */
  actor?: string
  /** Free-text target search across targetId and targetLabel */
  target?: string
  result?: CommandResultStatus
  /** ISO date/datetime lower bound (inclusive) */
  from?: string
  /** ISO date/datetime upper bound; date-only means through end of that day */
  to?: string
  /**
   * Operator timezone offset in minutes (`new Date().getTimezoneOffset()`).
   * Sent with date-only from/to so the API bounds the operator's LOCAL day;
   * omitted = UTC-day behavior.
   */
  tzOffset?: number
  limit?: number
  offset?: number
}

export interface SuspendUserParams {
  reason?: string
  /** Snapshot label (user email/name) persisted on the audit row */
  targetLabel?: string
}

export interface ExtendTrialParams {
  days: number
  targetLabel?: string
}

export interface GrantCreditsParams {
  amount: number
  reason?: string
  targetLabel?: string
}

export interface DeleteUserCommandParams {
  /** Snapshot label (user email/name) persisted on the audit row */
  targetLabel?: string
}

/** Params for the membership rescue commands (ids travel in the path). */
export interface MembershipCommandParams {
  /** Invited email snapshot persisted on the audit row */
  invitedEmail?: string
  /** Snapshot label (business name) persisted on the audit row */
  targetLabel?: string
}

/** Roles the cockpit can assign — mirrors UserRole on the server. */
export type AssignableUserRole = 'admin' | 'developer' | 'moderator' | 'system' | 'user'

export interface SetUserRoleCommandParams {
  role: AssignableUserRole
  /** Snapshot label (user email/name) persisted on the audit row */
  targetLabel?: string
}

export interface SetEngineDefaultParams {
  /** Engine key `engine_id@engine_version` (e.g. 'socialkit@v2') */
  engine: string
  /** The default being replaced — persisted on the audit row */
  previousEngine?: string
}

export interface SetEngineOverrideParams {
  /** Engine key to pin, or null to clear the override */
  engine: string | null
  /** Snapshot label (business name) persisted on the audit row */
  targetLabel?: string
}

export interface SetCompositionRendererOverrideParams {
  /** Master renderer to pin ('browser'), or null to clear the override */
  renderer: 'browser' | null
  /** Snapshot label (business name) persisted on the audit row */
  targetLabel?: string
}

export interface SetGatewayDefaultParams {
  map: Record<string, string>
  /** The map being replaced — persisted on the audit row */
  previousMap?: Record<string, string>
}

/**
 * Referral program config — mirrors the server's GET/PUT /v1/admin/referral-config
 * body. Caps bound the number of rewarded referrals per referrer.
 */
export interface ReferralConfig {
  // On/off is the `referral_program` PostHog flag — this config is knobs only.
  referrerCredits: number
  refereeCredits: number
  /** Max rewarded referrals per referrer in a rolling 30-day window */
  monthlyCap: number
  /** Max rewarded referrals per referrer over their lifetime */
  lifetimeCap: number
}

export interface SetGatewayOverrideParams {
  platform: string
  /** Gateway id to pin, or null to clear the override */
  gateway: string | null
  /** Snapshot label (business name) persisted on the audit row */
  targetLabel?: string
}

export interface CreateApiKeyCommandParams {
  name: string
  scopes: string[]
  expiresAt?: string
}

export interface ApiKeyCommandParams {
  /** Snapshot label (key name) persisted on the audit row */
  targetLabel?: string
}

export interface UpdateClientPolicyCommandParams {
  platform: string
  minimumVersion: { version: string; message?: string }
  maintenanceMode: { enabled: boolean; message?: string }
}

/** Mirrors the server's promo code vocabularies (promo-code.const.ts). */
export type PromoCodeCommandType = 'free_access' | 'discount'
export type PromoCodeCommandTier = 'Pro' | 'Business'
export type PromoCodeCommandDuration =
  | 'weekly'
  | 'two_week'
  | 'monthly'
  | 'two_month'
  | 'three_month'
  | 'six_month'
  | 'yearly'
  | 'lifetime'

export interface CreatePromoCodeCommandParams {
  /** Stored uppercase; 3-32 chars of letters, digits, _ or - */
  code: string
  type: PromoCodeCommandType
  /** Tier for free_access codes */
  tier?: PromoCodeCommandTier
  /** Grant length for free_access codes */
  duration?: PromoCodeCommandDuration
  /** RevenueCat offering id for discount codes */
  rcOfferingId?: string
  campaign?: string
  notes?: string
  /** Redemption cap; omit for unlimited */
  maxRedemptions?: number
  /** ISO expiry; omit for no expiry */
  validUntil?: string
}

export interface UpdatePromoCodeCommandParams {
  /** Kill switch */
  enabled?: boolean
  /** Redemption cap; null clears it */
  maxRedemptions?: number | null
  /** ISO expiry; null clears it */
  validUntil?: string | null
  campaign?: string
  notes?: string
  /** Snapshot label (the code string) persisted on the audit row */
  targetLabel?: string
}

/** Query key of the Command Log page's react-query cache (see hooks/use-command.ts). */
export const COMMAND_LOG_QUERY_KEY = ['command', 'log'] as const

/**
 * Query key prefix of the business timeline's sources (hooks/use-business-timeline.ts)
 * — invalidated after every audited command so receipts appear immediately
 * under the button that created them.
 */
export const BUSINESS_TIMELINE_QUERY_PREFIX = ['business-timeline'] as const

/**
 * Query key prefix of the user timeline's admin-receipts source
 * (hooks/use-user-timeline.ts) — same invalidation contract as above.
 */
export const USER_TIMELINE_ADMIN_QUERY_PREFIX = ['user-timeline', 'admin'] as const

/**
 * Execute one audited command. Every command writes an admin_actions row on
 * success AND failure, so the cached command log AND the entity timelines
 * that render receipts are invalidated regardless of outcome — the receipt
 * must show up immediately, even for call sites outside react-query (Redux
 * slices, plain-fetch pages).
 */
async function executeCommand(endpoint: string, params: unknown): Promise<CommandResult> {
  try {
    return await birdbrainApiFetch<CommandResult>(endpoint, {
      method: 'POST',
      body: JSON.stringify(params),
    })
  } finally {
    void queryClient.invalidateQueries({ queryKey: COMMAND_LOG_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: BUSINESS_TIMELINE_QUERY_PREFIX })
    void queryClient.invalidateQueries({ queryKey: USER_TIMELINE_ADMIN_QUERY_PREFIX })
  }
}

export const commandApi = {
  async suspendUser(userId: string, params: SuspendUserParams = {}): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/users/${encodeURIComponent(userId)}/suspend`,
      params
    )
  },

  async unsuspendUser(userId: string, params: SuspendUserParams = {}): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/users/${encodeURIComponent(userId)}/unsuspend`,
      params
    )
  },

  async extendTrial(userId: string, params: ExtendTrialParams): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/users/${encodeURIComponent(userId)}/extend-trial`,
      params
    )
  },

  async grantCredits(businessId: string, params: GrantCreditsParams): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/businesses/${encodeURIComponent(businessId)}/grant-credits`,
      params
    )
  },

  /** Flip a stuck invited membership to active via the audited proxy (action 'membership.activate'). */
  async activateMembership(
    businessId: string,
    membershipId: string,
    params: MembershipCommandParams = {}
  ): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/businesses/${encodeURIComponent(businessId)}/members/${encodeURIComponent(membershipId)}/activate`,
      params
    )
  },

  /** Mint a fresh token + resend the invite email via the audited proxy (action 'membership.reissue_invite'). */
  async reissueInvite(
    businessId: string,
    membershipId: string,
    params: MembershipCommandParams = {}
  ): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/businesses/${encodeURIComponent(businessId)}/members/${encodeURIComponent(membershipId)}/reissue-invite`,
      params
    )
  },

  /** Hard-delete a user via the audited proxy (action 'user.delete'). */
  async deleteUser(userId: string, params: DeleteUserCommandParams = {}): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/users/${encodeURIComponent(userId)}/delete`,
      params
    )
  },

  /** Change a user's role via the audited proxy (action 'user.set_role'). */
  async setUserRole(userId: string, params: SetUserRoleCommandParams): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/users/${encodeURIComponent(userId)}/set-role`,
      params
    )
  },

  /** Set the global default engine via the audited proxy (action 'engine.set_default'). */
  async setEngineDefault(params: SetEngineDefaultParams): Promise<CommandResult> {
    return executeCommand('/v1/birdbrain/command/engine/set-default', params)
  },

  /** Set/clear a per-business engine override (actions 'engine.set_override' / 'engine.clear_override'). */
  async setEngineOverride(
    businessId: string,
    params: SetEngineOverrideParams
  ): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/businesses/${encodeURIComponent(businessId)}/engine-override`,
      params
    )
  },

  /**
   * Set/clear a per-business composition master renderer override
   * (actions 'composition_renderer.set_override' / 'composition_renderer.clear_override').
   */
  async setCompositionRendererOverride(
    businessId: string,
    params: SetCompositionRendererOverrideParams
  ): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/businesses/${encodeURIComponent(businessId)}/composition-renderer-override`,
      params
    )
  },

  /** Set the global gateway routing map via the audited proxy (action 'gateway.set_default'). */
  async setGatewayDefault(params: SetGatewayDefaultParams): Promise<CommandResult> {
    return executeCommand('/v1/birdbrain/command/gateways/set-default', params)
  },

  /** Set the referral program config via the audited proxy (action 'referral.set_config'). */
  async setReferralConfig(params: ReferralConfig): Promise<CommandResult> {
    return executeCommand('/v1/birdbrain/command/referral/set-config', params)
  },

  /** Set/clear a per-business gateway override (actions 'gateway.set_override' / 'gateway.clear_override'). */
  async setGatewayOverride(
    businessId: string,
    params: SetGatewayOverrideParams
  ): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/businesses/${encodeURIComponent(businessId)}/gateway-override`,
      params
    )
  },

  /** Create an API key via the audited proxy (action 'api_key.create'). */
  async createApiKey(params: CreateApiKeyCommandParams): Promise<CommandResult> {
    return executeCommand('/v1/birdbrain/command/api-keys/create', params)
  },

  /** Revoke (deactivate) an API key via the audited proxy (action 'api_key.revoke'). */
  async revokeApiKey(keyId: string, params: ApiKeyCommandParams = {}): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/api-keys/${encodeURIComponent(keyId)}/revoke`,
      params
    )
  },

  /** Permanently delete an API key via the audited proxy (action 'api_key.delete'). */
  async deleteApiKey(keyId: string, params: ApiKeyCommandParams = {}): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/api-keys/${encodeURIComponent(keyId)}/delete`,
      params
    )
  },

  /** Update a platform client policy via the audited proxy (action 'client_policy.update'). */
  async updateClientPolicy(params: UpdateClientPolicyCommandParams): Promise<CommandResult> {
    return executeCommand('/v1/birdbrain/command/client-policy/update', params)
  },

  /** Create a promo code via the audited proxy (action 'promo_code.create'). */
  async createPromoCode(params: CreatePromoCodeCommandParams): Promise<CommandResult> {
    return executeCommand('/v1/birdbrain/command/promo-codes/create', params)
  },

  /** Update a promo code via the audited proxy (actions 'promo_code.update' / 'promo_code.enable' / 'promo_code.disable'). */
  async updatePromoCode(
    promoCodeId: string,
    params: UpdatePromoCodeCommandParams
  ): Promise<CommandResult> {
    return executeCommand(
      `/v1/birdbrain/command/promo-codes/${encodeURIComponent(promoCodeId)}/update`,
      params
    )
  },

  async getCommandLog(params: FetchCommandLogParams = {}): Promise<CommandLogResponse> {
    const queryParams = new URLSearchParams()
    if (params.targetType) queryParams.set('targetType', params.targetType)
    if (params.targetId) queryParams.set('targetId', params.targetId)
    if (params.action) queryParams.set('action', params.action)
    if (params.actor) queryParams.set('actor', params.actor)
    if (params.target) queryParams.set('target', params.target)
    if (params.result) queryParams.set('result', params.result)
    if (params.from) queryParams.set('from', params.from)
    if (params.to) queryParams.set('to', params.to)
    if (params.tzOffset != null) queryParams.set('tzOffset', params.tzOffset.toString())
    if (params.limit) queryParams.set('limit', params.limit.toString())
    if (params.offset != null) queryParams.set('offset', params.offset.toString())

    const queryString = queryParams.toString()
    const endpoint = `/v1/birdbrain/command/log${queryString ? `?${queryString}` : ''}`
    return birdbrainApiFetch<CommandLogResponse>(endpoint)
  },
}
